import { useRef, useCallback, useEffect } from 'react';
import type { MessageRule, CoordinateConfig, MessageInstance } from '@k-ucap/shared';

interface WorkerMessage {
  type: 'CHUNK' | 'PROGRESS' | 'COMPLETE' | 'ERROR';
  chunk?: MessageInstance[];
  totalGenerated?: number;
  cancelled?: boolean;
  error?: string;
}

export interface UseBulkMessageWorkerOptions {
  onChunk?: (chunk: MessageInstance[]) => void | Promise<void>;
  onProgress?: (totalGenerated: number) => void;
  onComplete?: (totalGenerated: number, cancelled: boolean) => void;
  onError?: (error: string) => void;
}

export const useBulkMessageWorker = (options: UseBulkMessageWorkerOptions) => {
  const workerRef = useRef<Worker | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  const start = useCallback((rule: MessageRule, count: number, defaultCoordinate: CoordinateConfig, seed?: string) => {
    if (workerRef.current) {
      workerRef.current.terminate();
    }
    const worker = new Worker(new URL('@/workers/messageGeneration.worker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;

    worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
      const data = event.data;
      if (data.type === 'CHUNK') {
        const chunk = data.chunk ?? [];
        void Promise.resolve(optionsRef.current.onChunk?.(chunk));
      } else if (data.type === 'PROGRESS') {
        optionsRef.current.onProgress?.(data.totalGenerated ?? 0);
      } else if (data.type === 'COMPLETE') {
        optionsRef.current.onComplete?.(data.totalGenerated ?? 0, data.cancelled ?? false);
      } else if (data.type === 'ERROR') {
        optionsRef.current.onError?.(data.error ?? 'Unknown worker error');
      }
    };

    worker.onerror = (error) => {
      optionsRef.current.onError?.(error.message || 'Worker error');
    };

    worker.postMessage({
      type: 'START',
      rule,
      count,
      chunkSize: 1000,
      defaultCoordinate,
      seed,
    });
  }, []);

  const cancel = useCallback(() => {
    workerRef.current?.postMessage({ type: 'CANCEL' });
  }, []);

  return { start, cancel };
};

export default useBulkMessageWorker;
